/**
 * Comments Store
 * Zustand store for managing comments state with real-time updates
 */

import type { CommentRecord } from "@shared/types";
import { create } from "zustand";
import { getProjectComments } from "../services/comments-api.service";

interface CommentsState {
	comments: CommentRecord[];
	isLoading: boolean;
	error: string | null;
	total: number;
	page: number;
	limit: number;
	hasMore: boolean;
	lastSeenCreatedAt: string | null;
	isLoaded: boolean;
	isConnecting: boolean;
	projectId: string | null;

	// Actions
	setComments: (
		comments: CommentRecord[],
		total: number,
		page: number,
		limit: number,
		hasMore: boolean,
	) => void;
	initFromDto: (comments: CommentRecord[]) => void;
	setProjectId: (projectId: string) => void;
	addComment: (comment: CommentRecord) => void;
	appendRealtime: (comment: CommentRecord) => void;
	catchUp: (after?: string | null) => Promise<void>;
	setLoading: (isLoading: boolean) => void;
	setError: (error: string | null) => void;
	setConnecting: (connecting: boolean) => void;
	reset: () => void;
}

const initialState = {
	comments: [] as CommentRecord[],
	isLoading: false,
	error: null as string | null,
	total: 0,
	page: 1,
	limit: 20,
	hasMore: false,
	lastSeenCreatedAt: null as string | null,
	isLoaded: false,
	isConnecting: false,
	projectId: null as string | null,
};

function getLatestCreatedAt(comments: CommentRecord[]): string | null {
	if (comments.length === 0) return null;
	// biome-ignore lint/style/noNonNullAssertion: length check above guarantees comments[0] exists
	let latest = comments[0]!.createdAt;
	for (const c of comments) {
		if (c.createdAt > latest) latest = c.createdAt;
	}
	return latest;
}

export const useCommentsStore = create<CommentsState>((set, get) => ({
	...initialState,

	setComments: (comments, total, page, limit, hasMore) =>
		set({
			comments,
			total,
			page,
			limit,
			hasMore,
			isLoading: false,
			error: null,
			isLoaded: true,
			lastSeenCreatedAt: getLatestCreatedAt(comments),
		}),

	initFromDto: (comments) =>
		set({
			comments,
			total: comments.length,
			page: 1,
			limit: 20,
			hasMore: false,
			isLoading: false,
			error: null,
			isLoaded: true,
			lastSeenCreatedAt: getLatestCreatedAt(comments),
		}),

	addComment: (comment) =>
		set((state) => ({
			// Add new comment at the beginning (newest first)
			comments: [comment, ...state.comments],
			total: state.total + 1,
			lastSeenCreatedAt: comment.createdAt,
		})),

	appendRealtime: (comment) => {
		// Check if comment already exists (avoid duplicates)
		const existing = get().comments;
		if (existing.some((c) => c.id === comment.id)) {
			return;
		}
		set({
			comments: [comment, ...existing],
			lastSeenCreatedAt: comment.createdAt,
		});
	},

	catchUp: async (after) => {
		const projectId = get().projectId;
		if (!projectId) return;

		const afterCursor = after ?? get().lastSeenCreatedAt;
		if (!afterCursor) return;

		try {
			const incoming = await getProjectComments(projectId, {
				after: afterCursor,
				limit: 100,
			});
			if (incoming.comments.length === 0) return;

			const existing = get().comments;
			const existingIds = new Set(existing.map((c) => c.id));
			const newComments = incoming.comments.filter(
				(c) => !existingIds.has(c.id),
			);

			if (newComments.length === 0) return;

			set({
				comments: [...newComments, ...existing],
				lastSeenCreatedAt: getLatestCreatedAt([...newComments, ...existing]),
			});
		} catch {
			// Ignore catch-up errors
		}
	},

	setProjectId: (projectId) => set({ projectId }),

	setLoading: (isLoading) => set({ isLoading }),

	setError: (error) => set({ error, isLoading: false }),

	setConnecting: (connecting) => set({ isConnecting: connecting }),

	reset: () => set(initialState),
}));
